import React, {useState, useEffect} from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import { Button, TextField, Typography } from '@mui/material';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import Modal from '@mui/material/Modal';
import SaveIcon from '@mui/icons-material/Save';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

import { adminGetChallenge, adminUpdateChallenge, adminDeleteChallenge } from "../../api/Api";
import { useColors } from '../../theme/ColorModeContext';
import PageContainer from '../../shared/PageContainer';
import GlowCard from '../../shared/GlowCard';
import CyberpunkTitle from '../../shared/CyberpunkTitle';
import { challengeCategories } from '../Academy/skillTreeData';

interface PropsAdminChallenges {
    token: string;
}

const fieldSx = (colors: any) => ({
    '& .MuiOutlinedInput-root': {
        fontFamily: '"Share Tech Mono", monospace',
        fontSize: '0.9rem',
        color: colors.text.primary,
        '& fieldset': { borderColor: colors.border.default },
        '&:hover fieldset': { borderColor: colors.border.accent },
        '&.Mui-focused fieldset': { borderColor: colors.accent.cyan, boxShadow: `0 0 0 2px ${colors.accent.cyanGlow}` },
    },
    '& .MuiInputLabel-root': {
        fontFamily: '"Share Tech Mono", monospace',
        fontSize: '0.82rem',
        letterSpacing: '0.08em',
        color: colors.text.secondary,
        '&.Mui-focused': { color: colors.accent.cyan },
    },
});

const selectSx = (colors: any) => ({
    fontFamily: '"Share Tech Mono", monospace',
    fontSize: '0.9rem',
    color: colors.text.primary,
    '& .MuiOutlinedInput-notchedOutline': { borderColor: colors.border.default },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: colors.border.accent },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: colors.accent.cyan },
    '& .MuiSvgIcon-root': { color: colors.text.secondary },
});

const dockerImages = ["all:latest", "daml:latest", "sui:latest", "solana:latest", "bitcoin:latest", "cairo:latest"];

const AdminUpdateChallenge = (props: PropsAdminChallenges) => {
    const colors = useColors();
    const [openDelete, setOpenDelete] = useState(false);
    const [processing, setProcessing] = useState(false);
    const [message, setMessage] = useState("");
    const [name, setName] = useState("");
    const [app, setApp] = useState("");
    const [active, setActive] = useState(false);
    const [difficulty, setDifficulty] = useState(0);
    const [points, setPoints] = useState(0);
    const [type, setType] = useState(0);
    const [category, setCategory] = useState("");
    const [dockerImage, setDockerImage] = useState("all:latest");

    const token = props.token;
    const navigate = useNavigate();
    const { id } = useParams();

    useEffect(() => {
        const fetchData = async () => {
            const data = await adminGetChallenge(token, id);
            setName(data.name);
            setApp(data.app);
            setActive(data.active);
            setDifficulty(data.difficulty);
            setPoints(data.points);
            setType(data.type);
            setCategory(data.category);
            setDockerImage(data.docker_image || "all:latest");
        }
        fetchData();
    }, []);

    const handleUpdate = async (e: any) => {
        e.preventDefault();
        setProcessing(true);
        const response = await adminUpdateChallenge(token, id, {
            name, app, active, difficulty, points, type, category, docker_image: dockerImage.trim() || "all:latest"
        });
        setProcessing(false);
        setMessage(response.msg);
    };

    const handleDelete = async (e: any) => {
        e.preventDefault();
        await adminDeleteChallenge(token, id);
        navigate("/admin/challenges");
    };

    return (
        <PageContainer>
            <CyberpunkTitle
                segments={[
                    { label: 'Challenges', to: '/admin/challenges' },
                    { label: name || 'Edit' },
                ]}
                mb="32px"
            />

            <GlowCard accentTop glowOnHover={false} sx={{ maxWidth: '600px' }}>
                <Box sx={{ padding: '32px' }}>
                    <Box component="form" onSubmit={handleUpdate} noValidate>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                            <TextField
                                sx={fieldSx(colors)}
                                required
                                fullWidth
                                label="Name"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                autoFocus
                            />
                            <TextField
                                sx={fieldSx(colors)}
                                required
                                fullWidth
                                label="App"
                                value={app}
                                onChange={(e) => setApp(e.target.value)}
                            />
                            <FormControl fullWidth>
                                <InputLabel sx={{
                                    fontFamily: '"Share Tech Mono", monospace',
                                    fontSize: '0.82rem',
                                    color: colors.text.secondary,
                                    '&.Mui-focused': { color: colors.accent.cyan },
                                }}>Docker Image</InputLabel>
                                <Select
                                    value={dockerImage}
                                    label="Docker Image"
                                    onChange={(e) => setDockerImage(e.target.value)}
                                    sx={selectSx(colors)}
                                    MenuProps={{ PaperProps: { sx: { backgroundColor: colors.bg.surface, color: colors.text.primary } } }}
                                >
                                    {dockerImages.map(image => (
                                        <MenuItem key={image} value={image} sx={{ fontFamily: '"Share Tech Mono", monospace', fontSize: '0.85rem' }}>
                                            {image}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <Box sx={{ display: 'flex', gap: '16px' }}>
                                <TextField
                                    sx={{ ...fieldSx(colors), flex: 1 }}
                                    required
                                    label="Difficulty"
                                    type="number"
                                    value={difficulty}
                                    onChange={(e) => setDifficulty(Number(e.target.value))}
                                />
                                <TextField
                                    sx={{ ...fieldSx(colors), flex: 1 }}
                                    required
                                    label="Points"
                                    type="number"
                                    value={points}
                                    onChange={(e) => setPoints(Number(e.target.value))}
                                />
                            </Box>

                            <Box sx={{ display: 'flex', gap: '16px' }}>
                                <FormControl fullWidth>
                                    <InputLabel sx={{
                                        fontFamily: '"Share Tech Mono", monospace',
                                        fontSize: '0.82rem',
                                        color: colors.text.secondary,
                                        '&.Mui-focused': { color: colors.accent.cyan },
                                    }}>Type</InputLabel>
                                    <Select
                                        value={type}
                                        label="Type"
                                        onChange={(e) => setType(Number(e.target.value))}
                                        sx={selectSx(colors)}
                                        MenuProps={{ PaperProps: { sx: { backgroundColor: colors.bg.surface, color: colors.text.primary } } }}
                                    >
                                        <MenuItem value={0} sx={{ fontFamily: '"Share Tech Mono", monospace', fontSize: '0.85rem' }}>Academy</MenuItem>
                                        <MenuItem value={1} sx={{ fontFamily: '"Share Tech Mono", monospace', fontSize: '0.85rem' }}>Mission</MenuItem>
                                    </Select>
                                </FormControl>

                                <FormControl fullWidth>
                                    <InputLabel sx={{
                                        fontFamily: '"Share Tech Mono", monospace',
                                        fontSize: '0.82rem',
                                        color: colors.text.secondary,
                                        '&.Mui-focused': { color: colors.accent.cyan },
                                    }}>Category</InputLabel>
                                    <Select
                                        value={category}
                                        label="Category"
                                        onChange={(e) => setCategory(e.target.value)}
                                        sx={selectSx(colors)}
                                        MenuProps={{ PaperProps: { sx: { backgroundColor: colors.bg.surface, color: colors.text.primary } } }}
                                    >
                                        {challengeCategories.map(c => (
                                            <MenuItem key={c} value={c} sx={{ fontFamily: '"Share Tech Mono", monospace', fontSize: '0.85rem' }}>
                                                {c}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Box>

                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={active}
                                        onChange={(e) => setActive(e.target.checked)}
                                        sx={{
                                            '& .MuiSwitch-switchBase.Mui-checked': { color: colors.accent.cyan },
                                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: colors.accent.cyanDim },
                                        }}
                                    />
                                }
                                label={
                                    <Typography sx={{
                                        fontFamily: '"Share Tech Mono", monospace',
                                        fontSize: '0.82rem',
                                        letterSpacing: '0.08em',
                                        color: colors.text.secondary,
                                    }}>
                                        Active
                                    </Typography>
                                }
                            />

                            <Button
                                type="submit"
                                fullWidth
                                variant="outlined"
                                disabled={processing}
                                startIcon={processing ? <CircularProgress size={16} sx={{ color: colors.accent.cyan }} /> : <SaveIcon />}
                                sx={{
                                    borderColor: colors.accent.cyan,
                                    color: colors.accent.cyan,
                                    fontFamily: '"Share Tech Mono", monospace',
                                    fontWeight: 700,
                                    fontSize: '0.8rem',
                                    letterSpacing: '0.14em',
                                    textTransform: 'uppercase',
                                    padding: '12px',
                                    borderRadius: '6px',
                                    '&:hover': {
                                        backgroundColor: colors.accent.cyanGlow,
                                        borderColor: colors.accent.cyan,
                                        boxShadow: `0 0 20px ${colors.accent.cyan}33`,
                                    },
                                    '&:disabled': {
                                        borderColor: colors.border.default,
                                        color: colors.text.muted,
                                    },
                                }}
                            >
                                {processing ? 'Saving...' : 'Save Changes'}
                            </Button>

                            {/* Divider */}
                            <Box sx={{ height: '1px', backgroundColor: colors.border.subtle, margin: '4px 0' }} />

                            <Button
                                fullWidth
                                variant="outlined"
                                startIcon={<DeleteOutlineIcon />}
                                onClick={() => setOpenDelete(true)}
                                sx={{
                                    borderColor: colors.accent.red,
                                    color: colors.accent.red,
                                    fontFamily: '"Share Tech Mono", monospace',
                                    fontWeight: 700,
                                    fontSize: '0.8rem',
                                    letterSpacing: '0.14em',
                                    textTransform: 'uppercase',
                                    padding: '12px',
                                    borderRadius: '6px',
                                    '&:hover': {
                                        backgroundColor: colors.accent.redGlow,
                                        borderColor: colors.accent.red,
                                        boxShadow: `0 0 20px ${colors.accent.red}22`,
                                    },
                                }}
                            >
                                Delete Challenge
                            </Button>

                            {message && (
                                <Box sx={{
                                    padding: '12px 16px',
                                    borderRadius: '6px',
                                    backgroundColor: colors.accent.cyanGlow,
                                    border: `1px solid ${colors.accent.cyan}44`,
                                    fontFamily: '"Share Tech Mono", monospace',
                                    fontSize: '0.8rem',
                                    color: colors.accent.cyan,
                                    letterSpacing: '0.06em',
                                }}>
                                    {message}
                                </Box>
                            )}
                        </Box>
                    </Box>
                </Box>
            </GlowCard>

            {/* Delete Confirmation Modal */}
            <Modal
                open={openDelete}
                onClose={() => setOpenDelete(false)}
            >
                <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: { xs: '90%', sm: '400px' },
                    backgroundColor: colors.bg.secondary,
                    border: `1px solid ${colors.accent.red}55`,
                    borderRadius: '8px',
                    padding: '32px',
                    boxShadow: `0 0 40px ${colors.accent.red}22`,
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                        <WarningAmberIcon sx={{ color: colors.accent.red, fontSize: '1.5rem' }} />
                        <Typography sx={{
                            fontFamily: '"Orbitron", sans-serif',
                            fontWeight: 700,
                            fontSize: '1.1rem',
                            color: colors.text.primary,
                            letterSpacing: '0.04em',
                        }}>
                            Delete Challenge
                        </Typography>
                    </Box>

                    <Typography sx={{
                        fontFamily: '"Share Tech Mono", monospace',
                        fontSize: '0.82rem',
                        color: colors.text.secondary,
                        marginBottom: '24px',
                        lineHeight: 1.6,
                    }}>
                        Are you sure you want to delete <Box component="span" sx={{ color: colors.accent.cyan, fontWeight: 700 }}>{name}</Box>? This action cannot be undone.
                    </Typography>

                    <Box sx={{ display: 'flex', gap: '12px' }}>
                        <Button
                            fullWidth
                            variant="outlined"
                            onClick={() => setOpenDelete(false)}
                            sx={{
                                borderColor: colors.border.default,
                                color: colors.text.secondary,
                                fontFamily: '"Share Tech Mono", monospace',
                                fontWeight: 600,
                                fontSize: '0.78rem',
                                letterSpacing: '0.1em',
                                textTransform: 'uppercase',
                                '&:hover': {
                                    borderColor: colors.border.strong,
                                    backgroundColor: colors.bg.tertiary,
                                },
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            fullWidth
                            variant="outlined"
                            onClick={handleDelete}
                            startIcon={<DeleteOutlineIcon />}
                            sx={{
                                borderColor: colors.accent.red,
                                color: colors.accent.red,
                                fontFamily: '"Share Tech Mono", monospace',
                                fontWeight: 700,
                                fontSize: '0.78rem',
                                letterSpacing: '0.1em',
                                textTransform: 'uppercase',
                                '&:hover': {
                                    backgroundColor: colors.accent.redGlow,
                                    boxShadow: `0 0 16px ${colors.accent.red}33`,
                                },
                            }}
                        >
                            Delete
                        </Button>
                    </Box>
                </Box>
            </Modal>
        </PageContainer>
    );
};

export default AdminUpdateChallenge;
